import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  useTheme,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  InputAdornment,
  Alert,
  Snackbar,
  Badge,
  CardHeader,
  CardActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Message as MessageIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Sms as SmsIcon,
  TrendingUp as TrendingUpIcon,
  Reply as ReplyIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { frontendSMSService } from '../../services/frontendSMSService';
import { messageService } from '../../services/messageService';
import { PreOnboardingLead, FrontendSMS, SendSMSRequest } from '../../types/sms';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/profileService';
import HireMessenger from '../hire/HireMessenger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sms-tabpanel-${index}`}
      aria-labelledby={`sms-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FrontendSMSManager: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [leads, setLeads] = useState<PreOnboardingLead[]>([]);
  const [messages, setMessages] = useState<FrontendSMS[]>([]);
  const [chatMessages, setChatMessages] = useState<FrontendSMS[]>([]);
  const [unseenMessages, setUnseenMessages] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState<PreOnboardingLead | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [stats, setStats] = useState({
    totalMessages: 0,
    newMessages: 0,
    outboundMessages: 0
  });
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    const fetchRole = async () => {
      const profile = await profileService.getCurrentUserProfile();
      if (profile) setUserRole(profile.role);
    };
    fetchRole();
  }, []);

  const handleBack = useCallback(() => {
    if (userRole === 'admin') {
      navigate('/dashboard');
    } else if (userRole === 'owner') {
      navigate('/leaderdashboard');
    } else {
      navigate(-1);
    }
  }, [userRole, navigate]);

  useEffect(() => {
    // Initial fetch with loading indicator
    fetchData(true);
    
    // Poll for new messages every 30 seconds (silent refresh - no loading spinner)
    const intervalId = setInterval(() => {
      fetchData(false);
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  const fetchData = async (showLoading: boolean = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const [leadsData, messagesData, statsData] = await Promise.all([
        frontendSMSService.getLeads(),
        messageService.getMessages(),
        messageService.getMessageStats()
      ]);
      setLeads(leadsData);
      setMessages(messagesData);
      
      // Helper to check if message is unseen (handle both boolean and string) - defined at top level
      const isUnseenMessageHelper = (msg: FrontendSMS): boolean => {
        const seenValue: any = msg.seen; // Handle type coercion
        if (seenValue === false || seenValue === 'false' || seenValue === 'False' || seenValue === 0 || seenValue === '0') {
          return true;
        }
        if (seenValue === true || seenValue === 'true' || seenValue === 'True' || seenValue === 1 || seenValue === '1') {
          return false;
        }
        return true; // Default to unseen if unknown value
      };
      
      // Initialize unseen messages set - only for inbound messages with seen = false (handles string/boolean)
      const unseenSet = new Set<string>();
      messagesData.forEach(msg => {
        // Only count inbound messages that are unseen
        if (msg.direction === 'inbound' && isUnseenMessageHelper(msg)) {
          unseenSet.add(msg.number);
        }
      });
      setUnseenMessages(unseenSet);
      
      // Debug logging to help troubleshoot
      console.log('[FrontendSMSManager] Message counts:', {
        totalMessages: messagesData.length,
        inboundMessages: messagesData.filter(m => m.direction === 'inbound').length,
        outboundMessages: messagesData.filter(m => m.direction === 'outbound').length,
        unseenInboundMessages: messagesData.filter(m => m.direction === 'inbound' && !m.seen).length,
        unseenSetSize: unseenSet.size,
        unseenPhoneNumbers: Array.from(unseenSet),
        leadsCount: leadsData.length
      });
      
      // Helper to check if message is unseen (handle both boolean and string)
      const isUnseenMessage = (msg: FrontendSMS): boolean => {
        // Check seen field - handle both boolean and string
        const seenValue: any = msg.seen; // Handle type coercion
        if (seenValue === false || seenValue === 'false' || seenValue === 'False' || seenValue === 0 || seenValue === '0') {
          return true;
        }
        if (seenValue === true || seenValue === 'true' || seenValue === 'True' || seenValue === 1 || seenValue === '1') {
          return false;
        }
        // Default to unseen if unknown value
        return true;
      };
      
      // Calculate new messages - ONLY inbound messages with seen = false (handles string/boolean)
      const unseenInboundMessages = messagesData.filter(msg => 
        msg.direction === 'inbound' && isUnseenMessage(msg)
      );
      const newMessages = unseenInboundMessages.length;
      
      // Calculate stats from actual messages data (filtered by org_id)
      // Use messagesData.length instead of statsData to ensure consistency
      const totalMessages = messagesData.length;
      const outboundMessages = messagesData.filter(msg => msg.direction === 'outbound').length;
      
      // Debug: Log ALL messages to see what we're working with
      console.log('[FrontendSMSManager] ALL messages sample (first 10):', messagesData.slice(0, 10).map(m => ({
        id: m.id,
        number: m.number,
        seen: m.seen,
        seenType: typeof m.seen,
        direction: m.direction,
        created_at: m.created_at
      })));
      
      // Debug: Log ALL inbound messages and their seen status
      const allInbound = messagesData.filter(m => m.direction === 'inbound');
      console.log('[FrontendSMSManager] ALL inbound messages:', {
        total: allInbound.length,
        seenTrue: allInbound.filter(m => !isUnseenMessage(m)).length,
        seenFalse: allInbound.filter(m => isUnseenMessage(m)).length,
        sampleSeenValues: Array.from(new Set(allInbound.map(m => `${m.seen} (${typeof m.seen})`))).slice(0, 5)
      });
      
      // Debug: Log messages that should trigger indicators
      console.log('[FrontendSMSManager] Unseen inbound messages count:', unseenInboundMessages.length);
      if (unseenInboundMessages.length > 0 && unseenInboundMessages.length <= 20) {
        console.log('[FrontendSMSManager] Unseen inbound messages:', unseenInboundMessages.map(m => ({
          id: m.id,
          number: m.number,
          seen: m.seen,
          seenType: typeof m.seen,
          direction: m.direction,
          created_at: m.created_at
        })));
      }
      
      setStats({
        totalMessages,
        newMessages,
        outboundMessages
      });
      
      console.log('[FrontendSMSManager] Stats being set:', { totalMessages, newMessages, outboundMessages });
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessagesForLead = async (lead: PreOnboardingLead) => {
    try {
      setMessagesLoading(true);
      const leadMessages = await messageService.getMessagesForLead(lead.id);
      
      // Preserve any optimistic messages for this lead
      const optimisticMessages = chatMessages.filter(msg => 
        msg.number === lead.phone_number && msg.id.startsWith('temp-')
      );
      
      // Combine fetched messages with optimistic messages
      const allMessages = [...leadMessages, ...optimisticMessages];
      
      // Sort by created_at to maintain chronological order
      allMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      
      setChatMessages(allMessages);
    } catch (error) {
      console.error('Error fetching messages for lead:', error);
      showSnackbar('Error loading messages', 'error');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleLeadClick = async (lead: PreOnboardingLead) => {
    setSelectedLead(lead);
    setChatOpen(true);
    await fetchMessagesForLead(lead);
    
    // Mark messages as seen when opening chat
    try {
      await messageService.markMessagesAsSeen(lead.id);
      
      // Remove this lead's phone number from unseen messages set
      setUnseenMessages(prevUnseen => {
        const newUnseen = new Set(prevUnseen);
        newUnseen.delete(lead.phone_number);
        return newUnseen;
      });
      
      // Update messages state directly to mark them as seen
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.number === lead.phone_number && !msg.seen
            ? { ...msg, seen: true }
            : msg
        )
      );
      
      // Update stats to reflect new message count
      setStats(prevStats => ({
        ...prevStats,
        newMessages: prevStats.newMessages - messages.filter(msg => 
          msg.number === lead.phone_number && !msg.seen && msg.direction === 'inbound'
        ).length
      }));
      
    } catch (error) {
      console.error('Error marking messages as seen:', error);
    }
  };

  const handleCloseChat = () => {
    setChatOpen(false);
    setSelectedLead(null);
    setNewMessage('');
  };

  const handleSendMessage = async () => {
    if (!selectedLead || !newMessage.trim()) return;

    const messageText = newMessage.trim();
    
    // Create optimistic message immediately
    const optimisticMessage: FrontendSMS = {
      id: `temp-${Date.now()}`, // Temporary ID
      name: selectedLead.name,
      number: selectedLead.phone_number,
      email: selectedLead.email,
      message: messageText,
      cost: 0,
      direction: 'outbound',
      seen: true,
      created_at: new Date().toISOString()
    };

    // Show message immediately in UI (optimistic update)
    setChatMessages(prevMessages => [optimisticMessage, ...prevMessages]);
    setMessages(prevMessages => [optimisticMessage, ...prevMessages]);
    setNewMessage('');
    
    // Update stats immediately
    setStats(prevStats => ({
      ...prevStats,
      totalMessages: prevStats.totalMessages + 1,
      outboundMessages: prevStats.outboundMessages + 1
    }));

    try {
      setSending(true);
      const request: SendSMSRequest = {
        leadId: selectedLead.id,
        message: messageText,
        phoneNumber: selectedLead.phone_number
      };

      const response = await messageService.sendSMS(request);
      
      if (response.success) {
        showSnackbar('Message sent successfully', 'success');
        
        // Close the chat popup to force user to refresh
        setChatOpen(false);
        setSelectedLead(null);
        
        // Replace optimistic message with real one from database
        const realMessage: FrontendSMS = {
          ...optimisticMessage,
          id: response.smsId || optimisticMessage.id
        };
        
        // Update chat messages with real ID
        setChatMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === optimisticMessage.id ? realMessage : msg
          )
        );
        
        // Update main messages with real ID
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === optimisticMessage.id ? realMessage : msg
          )
        );
      } else {
        // Remove optimistic message on failure
        setChatMessages(prevMessages => 
          prevMessages.filter(msg => msg.id !== optimisticMessage.id)
        );
        setMessages(prevMessages => 
          prevMessages.filter(msg => msg.id !== optimisticMessage.id)
        );
        
        // Revert stats
        setStats(prevStats => ({
          ...prevStats,
          totalMessages: prevStats.totalMessages - 1,
          outboundMessages: prevStats.outboundMessages - 1
        }));
        
        showSnackbar(response.message || 'Failed to send message', 'error');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove optimistic message on error
      setChatMessages(prevMessages => 
        prevMessages.filter(msg => msg.id !== optimisticMessage.id)
      );
      setMessages(prevMessages => 
        prevMessages.filter(msg => msg.id !== optimisticMessage.id)
      );
      
      // Revert stats
      setStats(prevStats => ({
        ...prevStats,
        totalMessages: prevStats.totalMessages - 1,
        outboundMessages: prevStats.outboundMessages - 1
      }));
      
      showSnackbar('Error sending message', 'error');
    } finally {
      setSending(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDirectionColor = (direction: string) => {
    return direction === 'inbound' ? 'primary' : 'secondary';
  };

  const getDirectionIcon = (direction: string) => {
    return direction === 'inbound' ? <ReplyIcon /> : <SendIcon />;
  };

  // Normalize phone number for comparison (remove formatting characters)
  const normalizePhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return '';
    // Remove all non-digit characters, then remove leading country code if present
    const digitsOnly = phone.replace(/\D/g, '');
    // Remove leading 1 if it's a US number (11 digits), keep as-is if 10 digits
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return digitsOnly.substring(1);
    }
    return digitsOnly;
  };

  const hasNewMessages = (lead: PreOnboardingLead) => {
    if (!lead.phone_number) return false;
    const normalizedLeadPhone = normalizePhoneNumber(lead.phone_number);
    
    // Check if any unseen message's number matches this lead's phone number
    // Try both exact match and normalized match
    return Array.from(unseenMessages).some(msgNumber => {
      if (msgNumber === lead.phone_number) return true; // Exact match
      const normalizedMsgPhone = normalizePhoneNumber(msgNumber);
      return normalizedLeadPhone && normalizedMsgPhone && normalizedLeadPhone === normalizedMsgPhone;
    });
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = search === '' || 
      lead.name.toLowerCase().includes(search.toLowerCase()) ||
      lead.email.toLowerCase().includes(search.toLowerCase()) ||
      (lead.phone_number && lead.phone_number.toLowerCase().includes(search.toLowerCase()));
    
    const matchesFilter = filter === 'all' ||
      (filter === 'jotform_filled' && lead.jotform_filled) ||
      (filter === 'follow_up_sent' && lead.follow_up_sent) ||
      (filter === 'pending' && !lead.jotform_filled && !lead.follow_up_sent);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ 
      mt: 4, 
      mb: 4,
      '@keyframes pulse': {
        '0%': {
          opacity: 1,
        },
        '50%': {
          opacity: 0.5,
        },
        '100%': {
          opacity: 1,
        },
      },
    }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton
              onClick={handleBack}
              sx={{
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              SMS Manager
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Manage leads and SMS communications
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Statistics Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 3, 
        mb: 3,
        '& > *': {
          flex: '1 1 250px',
          minWidth: '250px',
        }
      }}>
        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Unique Leads
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {leads.length}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                <PersonIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Total Messages
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {stats.totalMessages}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 56, height: 56 }}>
                <MessageIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>

        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  New Messages
                </Typography>
                <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                  {stats.newMessages}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 56, height: 56 }}>
                <WarningIcon />
              </Avatar>
            </Box>
          </CardContent>
        </Card>

      </Box>

      {/* Tabs */}
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Lead Messenger" />
          <Tab label="Hire Messenger" />
        </Tabs>

        <TabPanel value={selectedTab} index={0}>
          {/* Search and Filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search leads..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearch('')} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filter}
                label="Filter"
                onChange={(e) => setFilter(e.target.value)}
              >
                <MenuItem value="all">All Leads</MenuItem>
                <MenuItem value="jotform_filled">JotForm Filled</MenuItem>
                <MenuItem value="follow_up_sent">Follow-up Sent</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchData(true)}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {/* Leads Table */}
          <TableContainer 
            component={Paper} 
            elevation={2}
            sx={{ 
              maxHeight: '600px',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: 8,
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                },
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 120, 
                    position: 'sticky', 
                    left: 0, 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    zIndex: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 120,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Phone
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 140,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Calendly Time
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 150,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 100,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    JotForm
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 100,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Follow-up
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 120,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Created
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    minWidth: 100,
                    backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    Org ID
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow 
                    key={lead.id} 
                    hover
                    onClick={() => handleLeadClick(lead)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)' 
                          : 'rgba(102, 126, 234, 0.08)',
                      },
                    }}
                  >
                    <TableCell sx={{ 
                      position: 'sticky', 
                      left: 0, 
                      backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#ffffff',
                      zIndex: 1 
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {lead.name}
                        </Typography>
                        {hasNewMessages(lead) && (
                          <WarningIcon 
                            sx={{ 
                              color: theme.palette.warning.main, 
                              fontSize: '1rem',
                              animation: 'pulse 2s infinite'
                            }} 
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {lead.phone_number || 'No phone'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {lead.calendly_time ? formatDate(lead.calendly_time) : 'Not scheduled'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
                        {lead.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.jotform_filled ? 'Filled' : 'Not Filled'}
                        color={lead.jotform_filled ? 'success' : 'default'}
                        size="small"
                        icon={lead.jotform_filled ? <CheckCircleIcon /> : <CancelIcon />}
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.follow_up_sent ? 'Sent' : 'Not Sent'}
                        color={lead.follow_up_sent ? 'success' : 'default'}
                        size="small"
                        icon={lead.follow_up_sent ? <CheckCircleIcon /> : <CancelIcon />}
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {formatDate(lead.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                        {lead.org_id}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={selectedTab} index={1}>
          <HireMessenger />
        </TabPanel>

      </Paper>

      {/* Chat Dialog */}
      <Dialog
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">{selectedLead?.name}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {selectedLead?.email}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseChat}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
          {/* Messages List */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {messagesLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              <List>
                {chatMessages.map((message) => (
                  <ListItem
                    key={message.id}
                    sx={{
                      flexDirection: message.direction === 'outbound' ? 'row-reverse' : 'row',
                      alignItems: 'flex-start',
                    }}
                  >
                    <ListItemAvatar
                      sx={{
                        order: message.direction === 'outbound' ? 2 : 0,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: getDirectionColor(message.direction) + '.main',
                          width: 32,
                          height: 32,
                        }}
                      >
                        {getDirectionIcon(message.direction)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{
                        order: message.direction === 'outbound' ? 1 : 1,
                        textAlign: message.direction === 'outbound' ? 'right' : 'left',
                      }}
                      primary={
                        <Box
                          sx={{
                            backgroundColor: message.direction === 'outbound'
                              ? theme.palette.primary.main
                              : theme.palette.grey[300],
                            color: message.direction === 'outbound'
                              ? 'white'
                              : theme.palette.text.primary,
                            p: 2,
                            borderRadius: 2,
                            maxWidth: '70%',
                            display: 'inline-block',
                            textAlign: 'left',
                          }}
                        >
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              textAlign: 'left',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {message.message}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                            {formatDate(message.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Message Input */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                maxRows={3}
                disabled={sending}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                }}
              >
                {sending ? <CircularProgress size={20} /> : <SendIcon />}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FrontendSMSManager;
